/**
 * @description Configuração principal de roteamento da aplicação
 * @module Navigation
 * @since Fevereiro 2024
 */

import { createHashRouter, RouterProvider, Outlet } from "react-router-dom";
import {
  STORAGE_KEYS,
  MARKETPLACE_ERROR_TYPES,
  hasUrlParam,
  getUrlParamValue,
  setStorageFlag,
} from "../utils/urlParamManager";

import LoggedUser from "./components/LoggedUser";
import PatientQuestionnaireRoute from "./components/PatientQuestionnaireRoute";
import {
  SignIn,
  SignUp,
  Recover,
  NewPassword,
  ValidateDocument,
} from "../pages";
import SignupPatient from "../pages/signup/SignupPatient";

import { AuthContextProvider } from "../contexts/AuthContext";
import { PortalRoot } from "./components/PortalRoot";
import { PrivacyPolicy } from "@/pages/privacypolicy/PrivacyPolicy";

import RegularSignin from "@/pages/signin/RegularSignin";

import { PractitionerCards } from "@/pages/practitioners";
import PetAccess from "@/fluxvet/pages/petaccess/PetAccess";
import { DispensaryAccountLinkSuccessPage } from "src-next/modules/patient-supporting-documents/marketplace/components/DispensaryAccountLinkSuccessPage";
import { DISPENSARY_ACCOUNT_LINK_SUCCESS_ROUTE } from "src-next/modules/patient-supporting-documents/marketplace/constants/accountLinkSuccessRoute";
import { bootstrapSncrAuth } from "src-next/modules/sncr";

(() => {
  try {
    bootstrapSncrAuth();

    if (hasUrlParam("success")) {
      const successType = getUrlParamValue("success");
      if (successType === "authorized") {
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_SUCCESS);
      }
    }

    if (hasUrlParam("error")) {
      const errorType = getUrlParamValue("error");
      if (errorType) {
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR, "true");
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR_TYPE, errorType);
      }
    }
  } catch (error) {
  }
})();

/**
 * @description Configuração das rotas da aplicação usando createHashRouter
 * @remarks Utiliza hash router para melhor compatibilidade com servidores estáticos
 * @property future.v7_startTransition - Habilita o recurso de transição da v7 do React Router
 */
const router = createHashRouter(
  [
    // Rotas públicas (não requerem autenticação)
    {
      path: "/signin",
      element: <RegularSignin />,
    },
    {
      path: "/petaccess/:id",
      element: <PetAccess />,
    },
    {
      path: "/signup",
      element: <SignupPatient />,
    },
    {
      path: "/recover",
      element: <Recover />,
    },
    {
      path: "/available-practitioners",
      element: <PractitionerCards />,
    },
    {
      path: "/validateDocument/:id",
      element: <ValidateDocument />,
    },
    {
      path: "/newpassword",
      element: <NewPassword />,
    },
    {
      path: "/privacy-policy",
      element: <PrivacyPolicy />,
    },
    {
      path: DISPENSARY_ACCOUNT_LINK_SUCCESS_ROUTE,
      element: <DispensaryAccountLinkSuccessPage />,
    },
    // Rotas privadas (requerem autenticação via AuthContext)
    {
      path: "/",
      element: (
        <AuthContextProvider>
          <PortalRoot>
            <Outlet />
          </PortalRoot>
        </AuthContextProvider>
      ),
      children: [
        {
          index: true,
          element: <LoggedUser />,
        },
        {
          path: "patient-questionnaire/:questionnaireId",
          element: <PatientQuestionnaireRoute />,
        },
        {
          // Splat permite sub-rotas de página (ex.: /ecommerce/gerenciar/...).
          // useParams().id continua sendo o pageName do legado.
          path: ":id/*",
          element: <LoggedUser />,
        },
      ],
    },
  ],
  {
    future: {
      v7_normalizeFormMethod: true,
    },
  }
);

/**
 * @description Componente principal de navegação
 * @returns {JSX.Element} Router provider configurado com todas as rotas
 */
export default function Navigation() {
  return <RouterProvider router={router} />;
}
